import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles, CheckCircle2 } from 'lucide-react';
import { Button } from '../../../Components/ui/button';

/**
 * Final CTA — the last conversion surface before the footer.
 *
 * Design: deep-green ink panel (the same dark surface as the Security
 * band, so the page closes on the brand's premium dark note) with
 * emerald glow accents, grid texture and oversized rounded corners.
 *
 * Two CTAs with distinct destinations:
 *   • "Request a Demo"   → /hirewithus/contact (sales-led path)
 *   • "Start Free Trial" → /hirewithus/signup  (self-serve path)
 *
 * The reassurance row below the CTAs surfaces the four concerns most
 * enterprise buyers ask about: trial duration, commercial terms,
 * compliance posture, and customer success support.
 */
export default function FinalCTA() {
  const navigate = useNavigate();

  return (
    <section className="relative py-14 lg:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className="relative overflow-hidden rounded-[2.5rem] p-10 lg:p-16 text-white shadow-2xl"
          style={{ backgroundColor: 'hsl(164 33% 7%)' }}
        >
          {/* Emerald glow overlays */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-[-30%] right-[-10%] w-[60%] h-[80%] bg-emerald-500/15 rounded-full blur-3xl animate-cta-mesh-1" />
            <div className="absolute bottom-[-30%] left-[-10%] w-[50%] h-[70%] bg-teal-500/10 rounded-full blur-3xl animate-cta-mesh-2" />
          </div>

          {/* Grid overlay */}
          <div
            className="absolute inset-0 opacity-[0.07] pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
              backgroundSize: '44px 44px',
              maskImage:
                'radial-gradient(ellipse at center, black 30%, transparent 75%)',
              WebkitMaskImage:
                'radial-gradient(ellipse at center, black 30%, transparent 75%)',
            }}
          />

          <div className="relative max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/20 bg-white/10 backdrop-blur-sm text-xs font-semibold uppercase tracking-wider mb-6">
              <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
              Ready when you are
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight mb-5 leading-tight">
              Modernise your hiring
              <br />
              <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                with the right platform.
              </span>
            </h2>

            <p className="text-lg sm:text-xl text-white/70 leading-relaxed mb-8 max-w-2xl mx-auto">
              Schedule a personalised platform walkthrough with our team,
              or start a free trial and explore the platform on your own
              schedule.
            </p>

            {/* Demo requests route to the contact/sales page; the
                self-serve trial routes to signup. */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
              <Button
                size="lg"
                onClick={() => navigate('/hirewithus/contact')}
                className="!bg-white !text-foreground hover:!bg-white/90 h-12 px-8 text-base font-semibold group shadow-xl !rounded-full"
              >
                Request a Demo
                <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/hirewithus/demo')}
                className="!border-white/30 !bg-transparent !text-white hover:!bg-white/10 h-12 px-8 text-base font-semibold !rounded-full"
              >
                Try SearchJob
              </Button>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm text-white/80">
              <Reassurance text="No credit card required" />
              <Reassurance text="Encrypted data storage" />
              <Reassurance text="Dedicated onboarding" />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes cta-mesh-1 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(-30px, 20px) rotate(10deg); }
        }
        @keyframes cta-mesh-2 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(30px, -20px) rotate(-10deg); }
        }
        .animate-cta-mesh-1 { animation: cta-mesh-1 20s ease-in-out infinite; }
        .animate-cta-mesh-2 { animation: cta-mesh-2 24s ease-in-out infinite; }
      `}</style>
    </section>
  );
}

function Reassurance({ text }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
      {text}
    </span>
  );
}
